import type { CollectionActivityItem } from "./types";

/**
 * SAFE PAGINATION FOR AN ENDPOINT WHOSE PAGE METADATA IS FALSE.
 *
 * `GET /collections/{c}/activity` returns `{current_page: <echoed>, last_page: 1,
 * total: 20, per_page: 20}` on EVERY page, no matter which page was asked for —
 * measured against the live API on `0xbc4ca0…`, pages 1 through 10. Meanwhile the
 * rows themselves page correctly and deterministically: page 1 ends
 * `2026-08-03T20:14:47Z`, page 2 begins `2026-08-03T20:02:11Z`, and re-requesting
 * page 1 returns the identical window.
 *
 * So `last_page` cannot be the stop signal (it would hide every event after the
 * twentieth) and it cannot be trusted as a bound either (it says there is only
 * ever one page). With no authoritative end-of-list marker, termination has to be
 * derived from the ROWS — and, because a backend that lies about its metadata may
 * also page badly, from more than one property of them.
 *
 * FOUR INDEPENDENT TERMINATORS. Any one ends paging:
 *
 *   short-page      the last page returned fewer rows than the page size. The
 *                   honest end of the data.
 *   repeated-page   the endpoint re-served the previous window verbatim — same
 *                   keys, same order. It is not paging at all.
 *   no-new-events   the last page is FULL and not a verbatim repeat, but every
 *                   event in it has already been seen. A reshuffled duplicate
 *                   window: continuing would render nothing and fetch forever.
 *   cap             a conservative ceiling, for the case where every page is full
 *                   and genuinely new and there is therefore no data-derived
 *                   reason to ever stop.
 *
 * The last three mean the endpoint is not behaving as it claims, and the UI says
 * so rather than implying the history ended.
 *
 * This lives in its own module, free of React, so the rule can be unit-tested
 * directly — the cap in particular cannot be exercised through the UI without
 * twenty-five round trips.
 */

/** What the endpoint returns per page. Not configurable: it ignores `per_page`. */
export const ACTIVITY_PAGE_SIZE = 20;

/**
 * THE CONSERVATIVE SAFETY CAP: 25 pages, i.e. 500 events.
 *
 * Chosen to sit well past any plausible session of reading a transfer log by hand
 * and far short of a memory problem. It is a BOUND, not a claim about the data:
 * when it is reached the UI names the missing metadata instead of pretending the
 * history ran out.
 */
export const ACTIVITY_MAX_PAGES = 25;

export type ActivityStop = "more" | "short-page" | "no-new-events" | "repeated-page" | "cap";

/** Stable identity of one row — a transfer by (tx, log), a market record by its type and id. */
export { activityKey } from "./activity-events";
import { activityKey } from "./activity-events";

type ActivityPage = { data?: { list?: CollectionActivityItem[] } } | null | undefined;

export function activityStopReason(pages: ActivityPage[]): ActivityStop {
  if (pages.length === 0) return "more";
  // The cap is checked FIRST: once it is reached, why the data looked like it
  // could continue stops mattering.
  if (pages.length >= ACTIVITY_MAX_PAGES) return "cap";

  const rowsOf = (p: ActivityPage): CollectionActivityItem[] => p?.data?.list ?? [];
  const last = rowsOf(pages[pages.length - 1]);

  if (last.length < ACTIVITY_PAGE_SIZE) return "short-page";

  if (pages.length >= 2) {
    const prev = rowsOf(pages[pages.length - 2]);
    if (prev.length === last.length && prev.every((row, i) => activityKey(row) === activityKey(last[i]))) {
      return "repeated-page";
    }
  }

  const seen = new Set<string>();
  for (let i = 0; i < pages.length - 1; i++) rowsOf(pages[i]).forEach((row) => seen.add(activityKey(row)));
  if (seen.size > 0 && last.every((row) => seen.has(activityKey(row)))) return "no-new-events";

  return "more";
}
